import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { GetFunctionsByEventId, AddFunction, UpdateFunction, GetFunctionTypesByEventTypeId, GetAllFunctionTypes } from '../../api/api';
import { makeCrud } from '../../utils/crud';
import { useCrudList } from '../../utils/useCrudList';
import { FUNCTION_TYPES, OTHER_FUNCTION_TYPE, fmtDate, fmtTime, toTimeInput, isMarriageEventType } from '../../utils/helpers';

const today = () => new Date().toISOString().slice(0, 10);

// Functions = the sub-events that make up one Event (Haldi, Sangeet, Wedding Cum
// Reception, …). Per the role model, the Event Owner / Event Admin manages them;
// members below them only invite guests per function. Event-scoped.
const getColumns = (isTwoSided) => [
  { key: 'FunctionName', label: 'Function' },
  { key: 'FunctionType', label: 'Type' },
  { key: 'FunctionDate', label: 'Date', render: r => fmtDate(r.FunctionDate) },
  { key: 'StartTime', label: 'Time', render: r => (fmtTime(r.StartTime || r.FunctionTime) || '—') + (r.EndTime ? ` – ${fmtTime(r.EndTime)}` : '') },
  { key: 'Venue', label: 'Venue' },
  { key: 'Status', label: 'Status', render: r => <span className="chip pend">{r.Status}</span> },
];

const getFields = (isTwoSided, typeOptions) => [
  { key: 'FunctionName', label: 'Function Name', live: true, required: true, half: true },
  { key: 'FunctionType', label: 'Type', type: 'select', options: typeOptions, half: true },
  // Shown next to Type; used only when Type = "Other" (saved as a new type).
  { key: 'FunctionTypeOther', label: 'New type name (if Other)', live: true, half: true },
  // A function without a date cannot be sorted into the timetable, so it is
  // required and starts at today. Several functions can share a day, so both
  // ends of the slot are kept - date, from and to sit on one row.
  { key: 'FunctionDate', label: 'Date', type: 'date', required: true, default: today(), half: true },
  { key: 'StartTime', label: 'Start time', type: 'time', half: true },
  { key: 'EndTime', label: 'End time', type: 'time', half: true },
  { key: 'Venue', label: 'Venue', live: true, half: true },
  { key: 'City', label: 'City', live: true, half: true },
  { key: 'VenueAddress', label: 'Venue Address', type: 'textarea', rows: 2, span: 2 },
  { key: 'GpsLocation', label: 'GPS (lat,lng or Maps link — shown to guests)', type: 'textarea', rows: 2 },
  { key: 'Description', label: 'Description', type: 'textarea', rows: 2 },
];

export function useFunctions(event) {
  const { user, organiser, isAdmin } = useAuth();
  const nav = useNavigate();
  // Row click = drill in to the guests invited to this function (Esc comes back).
  const openRow = (row) => nav('/app/members?fn=' + row.FunctionId);
  const isTwoSided = isMarriageEventType(event?.EventTypeName || event?.EventType);
  const columns = useMemo(() => getColumns(isTwoSided), [isTwoSided]);

  // Function types for this event's type (global ones + its own, incl. names
  // created from this form earlier) + "Other" to type a new name.
  const [typeRows, setTypeRows] = useState([]);
  useEffect(() => {
    const req = event?.EventTypeId ? GetFunctionTypesByEventTypeId({ EventTypeId: event.EventTypeId }) : GetAllFunctionTypes();
    req.then(rows => setTypeRows(Array.isArray(rows) ? rows : [])).catch(() => setTypeRows([]));
  }, [event]);
  const typeOptions = useMemo(() => {
    const names = typeRows.length ? typeRows.map(r => r.FunctionTypeName) : FUNCTION_TYPES;
    // "Other" is the last entry we add ourselves; drop any master row of that
    // name (one was created by an early save) so the list has no duplicate key.
    const real = [...new Set(names.filter(Boolean))].filter(x => x !== OTHER_FUNCTION_TYPE);
    return real.concat(OTHER_FUNCTION_TYPE);
  }, [typeRows]);
  const fields = useMemo(() => getFields(isTwoSided, typeOptions), [isTwoSided, typeOptions]);

  const load = useCallback(() => GetFunctionsByEventId({ EventId: event.EventId })
    // Old rows may hold '7:00 PM' in FunctionTime and no StartTime — normalise
    // both so the clock inputs bind and the day sorts correctly.
    .then(rows => (Array.isArray(rows) ? rows : []).map(r => ({
      ...r,
      FunctionTime: toTimeInput(r.FunctionTime) || r.FunctionTime,
      StartTime: toTimeInput(r.StartTime || r.FunctionTime),
      EndTime: toTimeInput(r.EndTime),
    }))), [event]);
  const { onSave: rawSave, onDelete } = makeCrud({
    add: AddFunction, update: UpdateFunction, idKey: 'FunctionId',
    ctx: { EventId: event.EventId, OrganiserId: organiser.OrganiserId }, userId: user.UserId,
  });
  const onSave = useCallback((values, editing) => {
    const { FunctionTypeOther, ...rest } = values;
    // FunctionTime stays in step with StartTime (older screens still read it).
    rest.FunctionTime = rest.StartTime || rest.FunctionTime || '';
    // "Other" + a typed name → that name becomes the type (the API creates it).
    // "Other" is only a prompt to type the real name - saving it as the type
    // itself is what put a useless "Other" row in the master list.
    const typedName = String(FunctionTypeOther || '').trim();
    if (rest.FunctionType === OTHER_FUNCTION_TYPE && !typedName) {
      return Promise.reject(new Error('Type the new type name next to "Other"'));
    }
    const FunctionType = rest.FunctionType === OTHER_FUNCTION_TYPE ? typedName : rest.FunctionType;
    return rawSave({
      ...rest,
      FunctionType,
      // A function belongs to the event, not to a side — the SIDE lives on the
      // user (My Profile) and on the guests they add.
      Side: 'Common',
      Status: editing?.Status || rest.Status || 'Planned',
    }, editing).then(r => { setTypeRows(t => (t.some(x => x.FunctionTypeName === FunctionType) ? t : [...t, { FunctionTypeName: FunctionType }])); return r; });
  }, [rawSave]);

  // Owner / Event Admin (isAdmin = SuperAdmin, org admin, or per-event admin).
  const can = { add: isAdmin, edit: isAdmin, delete: isAdmin };

  const vm = useCrudList({
    load, onSave, onDelete, fields, can,
    confirmDelete: (r) => `Delete function "${r.FunctionName || ''}"?`,
  });
  // A day can hold several functions: group them, in time order, and flag two
  // that overlap so the planner notices while editing.
  const byDay = (vm.filtered || []).reduce((acc, row) => {
    const key = String(row.FunctionDate || '').slice(0, 10) || 'nodate';
    (acc[key] = acc[key] || []).push(row);
    return acc;
  }, {});
  const days = Object.keys(byDay)
    .sort((a, b) => (a === 'nodate' ? 1 : b === 'nodate' ? -1 : a.localeCompare(b)))
    .map(date => {
      const items = byDay[date].slice().sort((a, b) => String(a.StartTime || '').localeCompare(String(b.StartTime || '')));
      const clashes = new Set();
      items.forEach((a, i) => items.slice(i + 1).forEach(b => {
        if (!a.StartTime || !b.StartTime) return;
        const aEnd = a.EndTime || a.StartTime;
        if (b.StartTime < aEnd) { clashes.add(a.FunctionId); clashes.add(b.FunctionId); }
      }));
      return { date: date === 'nodate' ? '' : date, items, clashes };
    });

  // `event` goes out too: the form hangs the catering / services panel on it.
  return { ...vm, columns, isAdmin, isTwoSided, openRow, days, event };
}
